/**
 * Vzhľad appky (sada dizajnových tokenov v styles/tokens.css).
 *
 * Vyberá sa v Nastavenia → Vzhľad a pohyb (setVzhlad). Skratka `?vzhlad=b`
 * v adrese ostáva pre vývoj a na porovnávanie na vlastných dátach; zapamätá
 * sa rovnako, kým ho niekto nevráti cez `?vzhlad=a` (alebo `?vzhlad=reset`).
 *
 * Voľba platí pre zariadenie (localStorage), nie pre účet. Server nemá kam
 * ju uložiť bez zmeny schémy — `users` ani `notification_preferences` nemajú
 * voľný stĺpec pre nastavenia vzhľadu — a kvôli jednému písmenu sa migrácia
 * nerobí. Keď raz pribudne stĺpec s nastaveniami používateľa, localStorage
 * ostane rýchlou kópiou pre prvé vykreslenie a server zdrojom pravdy.
 *
 * Predvolený je vždy A. Keď úložisko nejde (súkromné okno v Safari, vypnuté
 * cookies), vzhľad sa neukladá, ale appka musí naštartovať — výnimka z
 * localStorage tu nesmie zhodiť prvé vykreslenie ani prepnutie v nastaveniach.
 */

export type Vzhlad = 'a' | 'b' | 'c';

export const VZHLAD_KEY = 'whoworks:vzhlad';
export const DEFAULT_VZHLAD: Vzhlad = 'a';
export const VZHLADY: ReadonlyArray<Vzhlad> = ['a', 'b', 'c'];
const KNOWN: ReadonlySet<string> = new Set(VZHLADY);

export const isVzhlad = (value: unknown): value is Vzhlad => typeof value === 'string' && KNOWN.has(value);

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/**
 * Farba lišty prehliadača / stavového riadku nainštalovanej appky. Rovnaká
 * ako `--ww-bar` v tokens.css (horná a spodná lišta na telefóne), aby lišta
 * systému a lišta appky splynuli do jedného pruhu. Hex, nie premenná: meta
 * tag CSS nečíta. Test (vzhlad.test.ts) stráži, že sa nerozíde s tokenmi.
 *
 * Manifest (public/manifest.webmanifest) je statický JSON a o vzhľade ani
 * režime nevie: background_color #0d0e10 a theme_color #111214 sú tmavé
 * plochy vzhľadu A. Tmavé zámerne — Android z nich kreslí úvodnú obrazovku
 * ešte pred načítaním stránky a krátky tmavý záblesk vo svetlom režime ruší
 * menej než biely v tmavom. Hneď po štarte lištu prevezme meta theme-color.
 * (Predtým tam bola navy #000616 zo starej značky, ktorá nesedela k ničomu.)
 */
export const THEME_COLOR: Record<Vzhlad, { light: string; dark: string }> = {
  a: { light: '#fafafb', dark: '#111214' },
  b: { light: '#f6f4f0', dark: '#13110f' },
  c: { light: '#fcfcff', dark: '#080a14' },
};

/**
 * Rozhodne vzhľad z adresy a úložiska. Parameter v adrese má prednosť a
 * uloží sa; neznáma hodnota sa ignoruje (preklep v odkaze nemá prepnúť nič).
 */
export const resolveVzhlad = (search: string, storage: StorageLike | null | undefined): Vzhlad => {
  let requested: string | null = null;
  try {
    requested = new URLSearchParams(search).get('vzhlad');
  } catch {
    requested = null;
  }
  const normalized = requested?.trim().toLowerCase() ?? null;

  if (normalized === 'reset') {
    try {
      storage?.removeItem(VZHLAD_KEY);
    } catch {
      // Nedá sa zapisovať — nič sa neuložilo, takže ani mazať netreba.
    }
    return DEFAULT_VZHLAD;
  }

  if (isVzhlad(normalized)) {
    try {
      storage?.setItem(VZHLAD_KEY, normalized);
    } catch {
      // Platí aspoň pre toto otvorenie.
    }
    return normalized;
  }

  try {
    const stored = storage?.getItem(VZHLAD_KEY);
    return isVzhlad(stored) ? stored : DEFAULT_VZHLAD;
  } catch {
    return DEFAULT_VZHLAD;
  }
};

/** Zapíše vzhľad na <html>, odkiaľ ho čítajú tokeny (`[data-vzhlad='b']`). */
export const applyVzhlad = (root: HTMLElement, vzhlad: Vzhlad): void => {
  root.dataset.vzhlad = vzhlad;
};

/** Aktuálny vzhľad podľa <html> — jediný zdroj pravdy počas behu. */
export const currentVzhlad = (doc: Document = document): Vzhlad => {
  const value = doc.documentElement.dataset.vzhlad;
  return isVzhlad(value) ? value : DEFAULT_VZHLAD;
};

/**
 * Nastaví <meta name="theme-color"> podľa vzhľadu a režimu.
 *
 * Dva meta tagy (media light/dark) sú v index.html kvôli štartu: kým beží
 * JavaScript, prehliadač si vyberie podľa systému. Keď si človek režim zvolil
 * sám (tmavý pri svetlom systéme), oba dostanú tú istú farbu — inak by lišta
 * systému ostala svetlá nad tmavou appkou. Pri režime „Systém“ dostane každý
 * svoju polovicu vzhľadu.
 *
 * Režim číta z `data-theme-mode` na <html> (zapisuje ho lib/theme.ts), aby sa
 * tieto dva moduly nemuseli navzájom importovať.
 */
export const syncThemeColor = (doc: Document = document): void => {
  const root = doc.documentElement;
  const colors = THEME_COLOR[currentVzhlad(doc)];
  const mode = root.dataset.themeMode;
  const resolved = root.classList.contains('dark') ? colors.dark : colors.light;
  const metas = doc.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]');
  metas.forEach((meta) => {
    const media = meta.getAttribute('media') ?? '';
    if (mode === 'system' && /dark/.test(media)) {
      meta.content = colors.dark;
    } else if (mode === 'system' && /light/.test(media)) {
      meta.content = colors.light;
    } else {
      meta.content = resolved;
    }
  });
};

const listeners = new Set<() => void>();

/** Pre useSyncExternalStore (hooks/useVzhlad). */
export const subscribeVzhlad = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const readStorage = (win: Window): StorageLike | null => {
  try {
    return win.localStorage;
  } catch {
    return null;
  }
};

/**
 * Prepne vzhľad hneď, bez načítania stránky: tokeny sú premenné, takže stačí
 * zmeniť atribút na <html>. Uloženie je až potom a smie zlyhať — voľba
 * potom platí do zatvorenia appky, čo je lepšie než tlačidlo, ktoré nič nerobí.
 */
export const setVzhlad = (vzhlad: Vzhlad, win: Window = window): void => {
  applyVzhlad(win.document.documentElement, vzhlad);
  syncThemeColor(win.document);
  try {
    const storage = readStorage(win);
    if (vzhlad === DEFAULT_VZHLAD) {
      storage?.removeItem(VZHLAD_KEY);
    } else {
      storage?.setItem(VZHLAD_KEY, vzhlad);
    }
  } catch {
    // Súkromné okno / plné úložisko — vzhľad platí aspoň teraz.
  }
  listeners.forEach((listener) => listener());
};

/**
 * Raz pri štarte, ešte pred prvým vykreslením — inak by stránka na zlomok
 * sekundy blikla vo vzhľade A.
 */
export const installVzhlad = (win: Window = window): Vzhlad => {
  const vzhlad = resolveVzhlad(win.location.search, readStorage(win));
  applyVzhlad(win.document.documentElement, vzhlad);
  syncThemeColor(win.document);
  return vzhlad;
};
